import { useEffect } from 'react';
import { useWizard } from '../wizardContext';
import { MIN_FINANCED_PERCENT } from '../types';
import { fmtBRL, fmtNum, onlyDigits } from '../utils/finance';

export const Step4Simulacao = () => {
  const { state, set, next, prev, entradaTotal, availableTerms, maxDownPayment, financedAmount } = useWizard();
  const s = state.simulation;
  const price = state.vehicle.price || 0;

  /* prefill seminovo ao entrar */
  useEffect(() => {
    set((d) => {
      if (d.hasTradeIn && d.tradeIn && !d.simulation.tradeInValue) d.simulation.tradeInValue = d.tradeIn.askingValue || 0;
      if (!d.hasTradeIn) d.simulation.tradeInValue = 0;
    });
  }, []);

  const setDown = (val: number) =>
    set((d) => {
      const maxDown = Math.max(
        0,
        price - (d.hasTradeIn ? d.simulation.tradeInValue || 0 : 0) - price * MIN_FINANCED_PERCENT,
      );
      d.simulation.downPayment = Math.max(0, Math.min(maxDown, val));
    });

  const ti = state.tradeIn;
  const tradeLabel = ti.brand ? `${ti.brand} ${ti.model} ${ti.year}` : 'Seminovo na negociação';

  return (
    <div className="lpfs-anim-in">
      <div className="lpfs-step-head">
        <h2 className="lpfs-step-title">Monte sua simulação</h2>
        <p className="lpfs-step-sub">Defina a entrada e o prazo. Os valores são atualizados em tempo real.</p>
      </div>

      <div className="lpfs-fieldset">
        <p className="lpfs-section-label">Valor de entrada</p>
        <div className="lpfs-slider-row">
          <div className="lpfs-money-input">
            <span>R$</span>
            <input
              inputMode="numeric"
              value={fmtNum(s.downPayment)}
              onChange={(e) => setDown(parseInt(onlyDigits(e.target.value) || '0', 10))}
            />
          </div>
        </div>
        <div className="lpfs-slider-wrap">
          <input
            type="range"
            className="lpfs-range"
            min={0}
            max={maxDownPayment}
            step={1}
            value={s.downPayment}
            style={{ ['--lpfs-fill' as string]: `${maxDownPayment > 0 ? (s.downPayment / maxDownPayment) * 100 : 0}%` }}
            onChange={(e) => setDown(parseInt(e.target.value, 10))}
          />
        </div>
        <div className="lpfs-slider-scale">
          <span>{fmtBRL(0)}</span>
          <span>{fmtBRL(maxDownPayment)}</span>
        </div>
      </div>

      {state.hasTradeIn && (
        <div className="lpfs-fieldset">
          <p className="lpfs-section-label">Seminovo na negociação</p>
          <p style={{ fontSize: 14, color: 'var(--lpfs-muted)', margin: '0 0 8px' }}>{tradeLabel}</p>
          <div className="lpfs-money-input">
            <span>R$</span>
            <input
              inputMode="numeric"
              value={fmtNum(s.tradeInValue)}
              onChange={(e) => {
                let val = parseInt(onlyDigits(e.target.value) || '0', 10);
                const maxTrade = Math.max(0, price - s.downPayment - price * MIN_FINANCED_PERCENT);
                if (val > maxTrade) val = maxTrade;
                set((d) => { d.simulation.tradeInValue = val; });
              }}
            />
          </div>
        </div>
      )}

      <div className="lpfs-fieldset">
        <p className="lpfs-section-label">Prazo desejado</p>
        <div className="lpfs-chips" role="radiogroup" aria-label="Prazo">
          {availableTerms.map((months) => {
            const active = months === s.term;
            return (
              <button
                type="button"
                key={months}
                role="radio"
                aria-checked={active}
                className={`lpfs-chip${active ? ' is-active' : ''}`}
                onClick={() => set((d) => { d.simulation.term = months; })}
              >
                {months}x
              </button>
            );
          })}
        </div>
      </div>

      <div className="lpfs-fieldset">
        <div className="lpfs-display" aria-live="polite">
          <div className="lpfs-display-entry">
            <span>Valor total da entrada</span>
            <strong>{fmtBRL(entradaTotal)}</strong>
          </div>
          <p className="lpfs-display-label">Valor total do financiamento</p>
          <p className="lpfs-display-value">{fmtBRL(financedAmount)}</p>
        </div>
      </div>

      <div className="lpfs-actions">
        <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={prev}>
          Voltar
        </button>
        <span className="lpfs-spacer" />
        <button type="button" className="lpfs-btn lpfs-btn-primary" onClick={next}>
          Simular
        </button>
      </div>
    </div>
  );
};
